/**
 * The selected definition's heading, and its Definition tab: the change
 * lifecycle and its checks, the effective YAML (the default branch's for an
 * enabled definition, with a banner and its diff on demand when an open MR
 * changes it; the MR's diff for a proposed one), and the actions.
 *
 * The heading takes focus when it mounts after the viewer's own selection
 * (`focusOnShow`), so a keyboard or phone user lands on the pane they just
 * opened (below the list, on a stacked phone layout) instead of on <body>.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { DefinitionActions, type DefinitionEditStart, type WithdrawOutcome } from './DefinitionActions';
import { DefinitionBadge } from './DefinitionBadge';
import { DefinitionDiff } from './DefinitionDiff';
import { DefinitionLifecycle } from './DefinitionLifecycle';
import type { DefinitionDetail as Detail } from '../types/projectPanel.types';

export function DefinitionHeader({ detail, focusOnShow }: { detail: Detail; focusOnShow: boolean }): ReactElement {
  const { summary } = detail;
  const pending = detail.state === 'enabled' ? summary.pending_mr : null;
  const heading = useRef<HTMLHeadingElement>(null);
  // Read once, on mount: a refetch never moves the viewer's focus.
  const focusOnMount = useRef(focusOnShow);
  useEffect(() => {
    if (focusOnMount.current) heading.current?.focus();
  }, []);
  return (
    <div className="def-detail__header">
      <h3 className="def-detail__name" ref={heading} tabIndex={-1}>{summary.name}</h3>
      <DefinitionBadge kind={detail.state} />
      {pending !== null && (
        <DefinitionBadge kind={pending.change === 'deleted' ? 'removal' : 'edit'} mrIid={pending.iid} />
      )}
    </div>
  );
}

function Yaml({ content }: { content: string | null }): ReactElement {
  if (content === null) return <p className="def-detail__hint">GitLab didn&apos;t return the file.</p>;
  return <pre className="def-yaml" aria-label="Definition YAML">{content}</pre>;
}

interface DefinitionTabProps {
  projectId: string;
  detail: Detail;
  open: string | null;
  refreshing: boolean;
  closedIid: number | null;
  onClosed: (iid: number) => void;
  onEdit?: (start: DefinitionEditStart) => void;
  editBlocked?: string | null;
  onWithdrawOutcome: (outcome: WithdrawOutcome | null) => void;
}

export function DefinitionTab(props: DefinitionTabProps): ReactElement {
  const { detail } = props;
  const [showDiff, setShowDiff] = useState(false);
  const pending = detail.state === 'enabled' ? detail.summary.pending_mr : null;
  return (
    <>
      {pending !== null && (
        <p className="def-detail__banner" role="note">
          {pending.change === 'deleted' ? 'Removal' : 'Edit'} pending in{' '}
          <a href={pending.url} target="_blank" rel="noreferrer" className="runs-page__link">
            !{pending.iid}
          </a>
          {detail.mr !== null && (
            <>
              {' · '}
              <button type="button" className="def-detail__link-button" aria-expanded={showDiff} onClick={() => { setShowDiff(!showDiff); }}>
                view diff
              </button>
            </>
          )}
        </p>
      )}
      <DefinitionLifecycle detail={detail} />
      {detail.state === 'proposed' && detail.mr !== null ? (
        <DefinitionDiff diff={detail.mr.diff} />
      ) : (
        <Yaml content={detail.content} />
      )}
      {detail.state === 'enabled' && showDiff && detail.mr !== null && <DefinitionDiff diff={detail.mr.diff} />}
      <DefinitionActions
        projectId={props.projectId}
        detail={detail}
        openUrl={props.open}
        refreshing={props.refreshing}
        editBlocked={props.editBlocked ?? null}
        closedIid={props.closedIid}
        onClosed={props.onClosed}
        onWithdrawOutcome={props.onWithdrawOutcome}
        {...(props.onEdit !== undefined ? { onEdit: props.onEdit } : {})}
      />
    </>
  );
}
